// Generated by TaroBase SDK Generator: 2026-10-04T15:00:51.103Z
// Collection: locks/$lockId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Multi-token time-locked escrow positions for SelfWorth. Users deposit SOL, BONK, SKR, MEW, USDC, GMT, ANGEL (AngelicSol), or DRAGON with a time lock, pay 1% deposit fee, and can unlock after timer expires or early-unlock with 1% penalty.
 */

export interface LocksRequest {
  owner: AddressType;
  depAmt: number | TimeOperation | IncrementOperation | TokenAmount;
  lkAmt: number | TimeOperation | IncrementOperation | TokenAmount;
  unlockAt: number | TimeOperation | IncrementOperation | TokenAmount;
  status: string;
  ts: number | TimeOperation | IncrementOperation | TokenAmount;
  lockType: string;
  tokenMint: string;
}

export interface LocksResponse {
  owner: string;
  depAmt: number;
  lkAmt: number;
  unlockAt: number;
  status: string;
  ts: number;
  lockType: string;
  tokenMint: string;
  id: string;
  tarobase_created_at: number;
  tarobase_transaction_hash?: string | undefined; // Represents the hash of the transaction that created this item. Can be used to create Solscan links (combine with the current network in the Tarobase app)
}

/**
 * Build a Locks operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildLocks(lockId: string, data: LocksRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `locks/${lockId}`, document: doc };
}

/**
 * Authenticated users create a lock by providing: owner (must match wallet), depAmt (deposit in base units of the token), lkAmt (must equal depAmt * 99 // 100), unlockAt (Unix seconds when lock expires), status (must be 'active'), lockType (must be 'standard' or 'perma', immutable after creation), tokenMint (must be one of @constants.SOL, @constants.BONK, @constants.SKR, @constants.MEW, @constants.USDC, @constants.GMT, @constants.ANGELIC_SOL, or @constants.DRAGON, immutable after creation), ts (creation timestamp). SECURITY: ts MUST be server time — the rule enforces @newData.ts within +/-300 seconds of @time.now, so a client cannot backdate or forward-date a lock. Clients MUST send Time.Now for ts (never a client clock value); any other value outside the 5-minute window is rejected. This prevents forging (unlockAt - ts) to inflate the duration score multiplier (up to 250x) and prevents shifting a lock into a different monthly raffle period. Hook transfers 1% fee to FEE_WALLET, creates a per-lock PDA escrow via @AccountPlugin.createAccount($lockId), and transfers 99% to that per-lock PDA (using $lockId directly as the destination) in the specified tokenMint. Each lock gets its own isolated PDA + per-mint associated token account created atomically. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setLocks(lockId: string, data: LocksRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`locks/${lockId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Locks: ${formatError(error)}`);
    return false;
  }
}

export type LocksRequestUpdate = Partial<LocksRequest>;

/**
 * Build a Locks update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateLocks(lockId: string, data: LocksRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `locks/${lockId}`, document: doc };
}

/**
 * Only the lock owner can update, and only while status is 'active'. Must set status to either 'unlocked' (requires @time.now >= unlockAt, returns full lkAmt to user from the per-lock PDA in the original token, works for both standard and perma locks) or 'earlyUnlocked' (requires @time.now < unlockAt and lockType == 'standard', charges 1% penalty from lkAmt to FEE_WALLET in original token, returns 99% of lkAmt to user). Funds are sourced from the per-lock PDA using $lockId directly as the transfer source. PermaLOCK locks (lockType == 'perma') CANNOT be early-unlocked and must wait until unlockAt expires. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateLocks(lockId: string, data: LocksRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`locks/${lockId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating Locks: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read all locks. All fields are publicly visible since this is an onchain collection.
   (Get Single Item)
 */
export async function getLocks(lockId: string): Promise<LocksResponse | null> {
  try {
    const result = await get(`locks/${lockId}`);
    return result as LocksResponse | null;
  } catch (error) {
    console.error(`Error getting Locks: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Locks document. (
  Read Operation Details: Anyone can read all locks. All fields are publicly visible since this is an onchain collection.
  )
 */
export function subscribeLocks(callback: (data: LocksResponse | null) => void, lockId: string): Promise<() => Promise<void>> {
  return subscribe(`locks/${lockId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as LocksResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Locks at path '${`locks/${lockId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Locks items from collection locks
 
  Read Operation Details: Anyone can read all locks. All fields are publicly visible since this is an onchain collection.
  
 */
export async function getManyLocks(filter: string = ""): Promise<LocksResponse[]> {
  try {
    const results = await get("locks", { prompt: filter });
    return Array.isArray(results) ? results as LocksResponse[] : [];
  } catch (error) {
    console.error(`Error getting Locks collection at path 'locks': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Locks collection at locks
 
  Read Operation Details: Anyone can read all locks. All fields are publicly visible since this is an onchain collection.
  
 */
export function subscribeManyLocks(callback: (data: LocksResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("locks", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as LocksResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Locks collection at path '${"locks"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Locks items from collection locks
 
  Read Operation Details: Anyone can read all locks. All fields are publicly visible since this is an onchain collection.
  
 */
export async function getAllLocks(filter: string = ""): Promise<LocksResponse[]> {
  try {
    const results = await get("locks", { prompt: filter });
    return Array.isArray(results) ? results as LocksResponse[] : [];
  } catch (error) {
    console.error(`Error getting Locks collection at path 'locks': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Locks collection at locks
 
  Read Operation Details: Anyone can read all locks. All fields are publicly visible since this is an onchain collection.
  
 */
export function subscribeAllLocks(callback: (data: LocksResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("locks", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as LocksResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Locks collection at path '${"locks"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count Locks items in collection locks.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countLocks(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("locks", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting Locks at path 'locks': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on Locks items in collection locks.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateLocks(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("locks", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating Locks at path 'locks': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Runs the "getVaultAddress" query on Locks.
 * Query Logic: @AccountPlugin.getAccountAddress($lockId)
 */
export async function runGetVaultAddressQueryForLocks(lockId: string, ): Promise<string> {
  try {
    const result = await runQuery(`locks/${lockId}`, "getVaultAddress", {});
    return result as string;
  } catch (error) {
    console.error(`Error running query "getVaultAddress" on Locks: ${formatError(error)}`);
    throw error;
  }
}

